import type { SelectChangeEvent, TextFieldVariants } from '@mui/material'
import { FormControl, InputLabel, MenuItem, Select, useTheme } from '@mui/material'
import React from 'react'
import { ThemeMode } from 'types/config'

interface TrueFalseSelectorProps {
  id?: string
  label?: string
  value?: boolean | string | number | null
  onChange: (value: boolean | null) => void
  name: string
  style?: React.CSSProperties // Add the optional style property
  pill?: boolean
  shrink?: boolean
  disabled?: boolean
  variant?: TextFieldVariants
  allowEmpty?: boolean
}

const TrueFalseSelector: React.FC<TrueFalseSelectorProps> = ({
  id,
  label,
  value,
  onChange,
  name,
  style,
  pill = false,
  shrink = false,
  disabled = false,
  variant = 'standard',
  allowEmpty = false,
}) => {
  const theme = useTheme()

  // Handle various boolean value formats
  const getBooleanValue = (inputValue: boolean | string | number | null | undefined): boolean | null => {
    // If allowEmpty and value is null/undefined/empty string, return null
    if (allowEmpty && (inputValue === null || inputValue === undefined || inputValue === '')) {
      return null
    }

    if (typeof inputValue === 'boolean') {
      return inputValue
    }
    if (typeof inputValue === 'string') {
      if (inputValue === '') {
        return allowEmpty ? null : false
      }
      const lowercaseValue = inputValue.toLowerCase()
      const truthyValues = ['true', '1', 't', 'y', 'yes']
      return truthyValues.includes(lowercaseValue)
    }
    if (typeof inputValue === 'number') {
      return inputValue !== 0
    }
    return allowEmpty ? null : false
  }

  const normalizedValue = getBooleanValue(value)

  const handleChange = (event: SelectChangeEvent<string>) => {
    if (event.target.value === '') {
      onChange(null)
    } else {
      onChange(event.target.value === 'true')
    }
  }

  const hoverBackgroundColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.secondary.dark

  return (
    <FormControl fullWidth variant={variant} style={style}>
      {label && (
        <InputLabel
          shrink={shrink}
          variant={variant}
          sx={{
            backgroundColor: theme.palette.background.default,
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        id={id}
        value={normalizedValue === null ? '' : normalizedValue.toString()}
        onChange={handleChange}
        name={name}
        variant={variant}
        sx={pill ? { borderRadius: '21px' } : undefined}
        MenuProps={{
          PaperProps: {
            sx: {
              '& .MuiMenuItem-root:hover': {
                backgroundColor: hoverBackgroundColor,
              },
            },
          },
        }}
        disabled={disabled}
        displayEmpty={allowEmpty}
      >
        {allowEmpty && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </Select>
    </FormControl>
  )
}

export default TrueFalseSelector
